import { Link, router } from '@inertiajs/react'
import { useEffect, useRef } from 'react'
import InfoTip from '../../../components/InfoTip'

// ─── Constants ────────────────────────────────────────────────────────────────

const CARRIER_COLORS = {
  B6: '#003087', DL: '#E01933', UA: '#005DAA', AA: '#0078D2', WN: '#304CB2',
}

const CARRIER_NAMES = {
  B6: 'JetBlue', DL: 'Delta', UA: 'United', AA: 'American', WN: 'Southwest',
}

// ─── Revenue bar chart ────────────────────────────────────────────────────────

function RevenueBar({ carrier, actual, projected }) {
  const color     = CARRIER_COLORS[carrier] || '#607D8B'
  const name      = CARRIER_NAMES[carrier] || carrier
  const maxVal    = Math.max(actual, projected, 0.01)
  const pctActual = Math.round((actual / maxVal) * 100)
  const pctProj   = Math.round((projected / maxVal) * 100)
  const delta     = actual - projected
  const deltaColor = delta >= 0 ? '#4CAF50' : '#F44336'

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      {/* Carrier header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-mono font-bold text-lg" style={{ color }}>{carrier}</span>
          <span className="text-sm text-gray-500">{name}</span>
        </div>
        <div className="text-right">
          <p className="text-xs font-semibold" style={{ color: deltaColor }}>
            {delta >= 0 ? '+' : ''}${delta.toFixed(2)}
          </p>
          <p className="text-xs text-gray-400">vs projected</p>
        </div>
      </div>

      {/* Actual bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500">Actual Revenue</span>
          <span className="text-xs font-bold tabular-nums text-gray-800">${actual.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div className="bg-gray-100 rounded-full h-4 overflow-hidden">
          <div
            className="h-full rounded-full flex items-center justify-end pr-2"
            style={{ width: `${pctActual}%`, background: color }}
          >
            {pctActual > 15 && (
              <span className="text-xs text-white font-bold">{pctActual}%</span>
            )}
          </div>
        </div>
      </div>

      {/* Projected bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500">Projected (vuuForge)</span>
          <span className="text-xs font-bold tabular-nums text-gray-500">${projected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div className="bg-gray-100 rounded-full h-4 overflow-hidden">
          <div
            className="h-full rounded-full flex items-center justify-end pr-2"
            style={{ width: `${pctProj}%`, background: color + '66', borderRight: `2px dashed ${color}` }}
          >
            {pctProj > 15 && (
              <span className="text-xs" style={{ color }}>{pctProj}%</span>
            )}
          </div>
        </div>
      </div>

      {/* Achievement rate */}
      {projected > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
          <span className="text-gray-400">Achievement Rate</span>
          <span className="font-bold tabular-nums" style={{ color: actual >= projected ? '#4CAF50' : '#FF9800' }}>
            {Math.round((actual / projected) * 100)}%
          </span>
        </div>
      )}
    </div>
  )
}

// ─── SVG comparative chart ────────────────────────────────────────────────────

function ComparisonChart({ carriers, actual, projected }) {
  const W = 560, H = 200, PAD_LEFT = 60, PAD_RIGHT = 20, PAD_TOP = 16, PAD_BOTTOM = 30
  const drawW = W - PAD_LEFT - PAD_RIGHT
  const drawH = H - PAD_TOP - PAD_BOTTOM

  const allValues = [
    ...carriers.map(c => actual[c] || 0),
    ...carriers.map(c => projected[c] || 0),
    0.01,
  ]
  const maxVal = Math.max(...allValues)

  const barGroupW = drawW / Math.max(carriers.length, 1)
  const barW      = Math.min(barGroupW * 0.35, 30)

  const yTicks = [0, 0.25, 0.5, 0.75, 1.0].map(t => ({
    y:     PAD_TOP + drawH - t * drawH,
    label: `$${(t * maxVal).toFixed(0)}`,
  }))

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
        Actual vs. Projected — All Carriers
      </p>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxWidth: W }}>
        {/* Y-axis ticks */}
        {yTicks.map(({ y, label }) => (
          <g key={label}>
            <line x1={PAD_LEFT - 4} y1={y} x2={W - PAD_RIGHT} y2={y} stroke="#F0F0F0" strokeWidth={1} />
            <text x={PAD_LEFT - 6} y={y + 4} textAnchor="end" fontSize={8} fill="#9E9E9E">{label}</text>
          </g>
        ))}

        {/* Bars */}
        {carriers.map((carrier, i) => {
          const groupX = PAD_LEFT + i * barGroupW
          const cx     = groupX + barGroupW / 2
          const act    = actual[carrier] || 0
          const proj   = projected[carrier] || 0
          const color  = CARRIER_COLORS[carrier] || '#607D8B'

          const actH  = (act / maxVal)  * drawH
          const projH = (proj / maxVal) * drawH
          const actY  = PAD_TOP + drawH - actH
          const projY = PAD_TOP + drawH - projH

          return (
            <g key={carrier}>
              {/* Projected bar (behind, lighter) */}
              <rect
                x={cx - barW - 1} y={projY}
                width={barW} height={Math.max(projH, 2)}
                fill={color + '44'} rx={2}
                stroke={color + '88'} strokeWidth={1} strokeDasharray="3 2"
              />
              {/* Actual bar */}
              <rect
                x={cx + 1} y={actY}
                width={barW} height={Math.max(actH, 2)}
                fill={color} rx={2}
              />
              {/* Label */}
              <text
                x={cx} y={H - PAD_BOTTOM + 12}
                textAnchor="middle" fontSize={9} fontWeight="700"
                fill={color}
              >
                {carrier}
              </text>
            </g>
          )
        })}

        {/* Legend */}
        <rect x={W - 130} y={PAD_TOP} width={10} height={10} fill="#9E9E9E44" stroke="#9E9E9E88" strokeDasharray="3 2" rx={2} />
        <text x={W - 116} y={PAD_TOP + 8} fontSize={8} fill="#9E9E9E">Projected</text>
        <rect x={W - 130} y={PAD_TOP + 16} width={10} height={10} fill="#607D8B" rx={2} />
        <text x={W - 116} y={PAD_TOP + 24} fontSize={8} fill="#9E9E9E">Actual</text>
      </svg>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AncRevenue({ carriers, actual, projected, selected_carrier, tenant_branding }) {
  const intervalRef = useRef(null)

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      router.reload({ only: ['carriers', 'actual', 'projected'] })
    }, 60_000)
    return () => clearInterval(intervalRef.current)
  }, [])

  const totalActual    = carriers.reduce((n, c) => n + (actual[c] || 0), 0)
  const totalProjected = carriers.reduce((n, c) => n + (projected[c] || 0), 0)
  const totalDelta     = totalActual - totalProjected

  function selectCarrier(carrier) {
    const params = carrier ? { carrier_code: carrier } : {}
    router.get('/admin/cloudtraq_gds/anc_revenue', params, { preserveScroll: true })
  }

  const displayCarriers = selected_carrier ? [selected_carrier] : carriers

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Anc Revenue</h1>
            <p className="text-sm text-gray-500 mt-1">
              Actual vs. vuuForge projections per carrier · {carriers.length} carriers
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => router.reload()} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums text-gray-800">
              ${totalActual.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-500">Total Actual Revenue</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums text-gray-500">
              ${totalProjected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-500">Total Projected</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ color: totalDelta >= 0 ? '#4CAF50' : '#F44336' }}>
              {totalDelta >= 0 ? '+' : ''}${totalDelta.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-500">Delta vs Projected</p>
          </div>
        </div>

        {/* Carrier filter */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button
            onClick={() => selectCarrier(null)}
            className={`text-xs px-3 py-1 rounded-full border font-medium ${!selected_carrier ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
          >
            All carriers
          </button>
          {carriers.map(c => (
            <button
              key={c}
              onClick={() => selectCarrier(c)}
              className={`text-xs px-3 py-1 rounded-full border font-bold ${selected_carrier === c ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
              style={selected_carrier === c ? { background: CARRIER_COLORS[c] || '#607D8B' } : {}}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Comparison chart (all carriers) */}
        {!selected_carrier && carriers.length > 1 && (
          <div className="mb-4">
            <ComparisonChart carriers={carriers} actual={actual} projected={projected} />
          </div>
        )}

        {/* Individual carrier cards */}
        {displayCarriers.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-400 text-sm">No revenue data available.</p>
            <p className="text-gray-400 text-xs mt-1">Revenue data appears when CloudtraqAd records are linked to PNRs.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {displayCarriers.map(carrier => (
              <RevenueBar
                key={carrier}
                carrier={carrier}
                actual={actual[carrier] || 0}
                projected={projected[carrier] || 0}
              />
            ))}
          </div>
        )}

        {/* Nav */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/carrier_metrics" className="text-blue-600 hover:underline">Carrier Metrics</Link>
          <Link href="/admin/cloudtraq_gds/pnr_mutations" className="text-blue-600 hover:underline">PNR Mutations</Link>
        </div>
      </div>
    </div>
  )
}
